import { useCallback, useState } from 'react';
import { Check, ChevronRight, Copy } from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatLotusUuidDateTime } from '@/shared/lib';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/shadcn/collapsible';

import type { DebugEvent } from '../model/types';

const CATEGORY_PALETTE = [
  'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
  'bg-orange-500/15 text-orange-700 dark:text-orange-300',
  'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300',
];

function hashCategory(category: string): number {
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = (hash * 31 + category.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

const FALLBACK_COLOR = 'bg-muted text-muted-foreground';

function categoryColorClass(category: string): string {
  return CATEGORY_PALETTE[hashCategory(category) % CATEGORY_PALETTE.length] ?? FALLBACK_COLOR;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

interface DebugEventRowProps {
  event: DebugEvent;
  relativeLabel: string;
}

export function DebugEventRow({ event, relativeLabel }: DebugEventRowProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const serialized = safeStringify(event.data);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(serialized).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [serialized]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border-b border-border">
      <CollapsibleTrigger className="flex w-full items-start gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-muted/50">
        <ChevronRight
          className={cn('mt-0.5 size-3 shrink-0 transition-transform', open && 'rotate-90')}
        />
        <span className="shrink-0 pt-px font-mono text-muted-foreground">{relativeLabel}</span>
        <span
          className={cn(
            'shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] leading-tight',
            categoryColorClass(event.category)
          )}
        >
          {event.category}
        </span>
        <span className="min-w-0 truncate text-foreground">{event.summary}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-t border-border bg-muted/30 px-3 py-2">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="font-mono text-[10px] text-muted-foreground">
              {formatLotusUuidDateTime(event.id) ?? '—'}
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleCopy}
              aria-label="Copy JSON to clipboard"
            >
              {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
            </Button>
          </div>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-foreground">
            {serialized}
          </pre>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
